import { Download, Upload, UserPlus, X } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SegmentButton } from '@/components/ui/segment-button';
import { Select } from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import {
  addDays,
  endOfDayMs,
  parseEmails,
  people,
  PLAN_OPTIONS,
  planLabel,
  shortDate,
} from './format';
import { NewTeamDialog } from './NewTeamDialog';
import { useAddPeople, usePeopleDryRun } from './useAccounts';

import type { PersonOutcome, PersonResult } from './accounts.types';

type TrialLength = 14 | 30 | 60 | 'custom';

const OUTCOMES: Record<PersonOutcome, { label: string; className: string }> = {
  new_account: {
    label: 'New account, trial now',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  trial_now: {
    label: 'Has an account, trial now',
    className: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  skipped_paid: {
    label: 'Already paying, skipped',
    className: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  skipped_team: {
    label: 'On a team, skipped',
    className: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  invalid: {
    label: 'Not an email',
    className: 'bg-red-100 text-red-700 border-red-200',
  },
  duplicate: {
    label: 'Duplicate',
    className: 'bg-zinc-200 text-zinc-700 border-zinc-300',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-700 border-red-200',
  },
};

function countOf(
  results: PersonResult[],
  ...outcomes: PersonOutcome[]
): number {
  return results.filter((result) => outcomes.includes(result.outcome)).length;
}

function emailsFromCsv(csv: string): string[] {
  const rows = csv
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((row) => row.split(',')[0].trim().replace(/^"|"$/g, ''));
  return rows[0]?.includes('@') ? rows : rows.slice(1);
}

function downloadCsv(results: PersonResult[]) {
  const csv = [
    'email,outcome,detail',
    ...results.map((r) => `${r.email},${r.outcome},"${r.detail ?? ''}"`),
  ].join('\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  link.download = 'add-people-results.csv';
  link.click();
}

interface AddPeopleSheetProps {
  onClose: () => void;
  onChooseTeam: () => void;
}

export function AddPeopleSheet({ onClose, onChooseTeam }: AddPeopleSheetProps) {
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[95vw] overflow-y-auto sm:max-w-xl">
        <AddPeopleSteps onChooseTeam={onChooseTeam} />
      </SheetContent>
    </Sheet>
  );
}

export function AddPeopleButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isNewTeamOpen, setIsNewTeamOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setIsOpen(true)}>
        <UserPlus className="mr-1 h-4 w-4" />
        Add people
      </Button>
      {isOpen && (
        <AddPeopleSheet
          onClose={() => setIsOpen(false)}
          onChooseTeam={() => {
            setIsOpen(false);
            setIsNewTeamOpen(true);
          }}
        />
      )}
      {isNewTeamOpen && (
        <NewTeamDialog onClose={() => setIsNewTeamOpen(false)} />
      )}
    </>
  );
}

function AddPeopleSteps({ onChooseTeam }: { onChooseTeam: () => void }) {
  const [step, setStep] = useState(1);
  const [text, setText] = useState('');
  const [checkedEmails, setCheckedEmails] = useState<string[]>([]);
  const [plan, setPlan] = useState('pro_monthly');
  const [trialLength, setTrialLength] = useState<TrialLength>(30);
  const [customDate, setCustomDate] = useState('');
  const [results, setResults] = useState<PersonResult[] | null>(null);
  const addPeople = useAddPeople();
  const toast = useToast();

  const trialEnd =
    trialLength === 'custom' ? customDate : addDays(Date.now(), trialLength);
  const trialEndLabel = trialEnd
    ? shortDate(endOfDayMs(trialEnd))
    : 'the end date';
  const dryRun = usePeopleDryRun({
    emails: checkedEmails,
    plan,
    trial_end: addDays(Date.now(), 30),
  });
  const preview = dryRun.data?.results ?? [];
  const newAccounts = countOf(preview, 'new_account');
  const trialNow = countOf(preview, 'trial_now');
  const skippedPaid = countOf(preview, 'skipped_paid');
  const skippedTeam = countOf(preview, 'skipped_team');
  const unusable = countOf(preview, 'invalid', 'duplicate');

  const check = (nextText: string) => {
    setText(nextText);
    setCheckedEmails(parseEmails(nextText));
  };

  const handleCsv = async (file: File | undefined) => {
    if (file)
      check(
        [text, ...emailsFromCsv(await file.text())].filter(Boolean).join('\n')
      );
  };

  const handleConfirm = async () => {
    try {
      const response = await addPeople.mutateAsync({
        emails: checkedEmails,
        plan,
        trial_end: trialEnd,
        dry_run: false,
      });
      setResults(response.results);
    } catch (e) {
      toast.error(
        'Could not add people',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  if (results) {
    return (
      <div className="space-y-4">
        <SheetHeader>
          <SheetTitle>Done</SheetTitle>
          <SheetDescription>
            {people(countOf(results, 'new_account', 'trial_now'))} added. Here
            is what happened to each email.
          </SheetDescription>
        </SheetHeader>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadCsv(results)}
        >
          <Download className="mr-1 h-4 w-4" />
          Download CSV
        </Button>
        <ul className="divide-y rounded-md border text-sm">
          {results.map((result, index) => (
            <li
              key={index}
              className="flex items-center justify-between gap-2 px-3 py-2"
            >
              <span>{result.email}</span>
              <span
                className={cn(
                  'rounded-full border px-2 py-0.5 text-xs',
                  OUTCOMES[result.outcome].className
                )}
              >
                {result.detail ?? OUTCOMES[result.outcome].label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SheetHeader>
        <SheetTitle>Add people</SheetTitle>
        <SheetDescription>Step {step} of 3</SheetDescription>
      </SheetHeader>

      {step === 1 && (
        <div className="space-y-3">
          <p className="text-sm font-medium">Who are you adding?</p>
          <Textarea
            rows={8}
            placeholder="Paste emails, separated by commas, spaces or new lines"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => check(text)}
          />
          <label className="inline-flex cursor-pointer items-center gap-1 text-sm text-primary">
            <Upload className="h-4 w-4" />
            Upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleCsv(e.target.files?.[0])}
            />
          </label>
          {preview.length > 0 && (
            <>
              <p className="text-sm text-muted-foreground">
                {preview.length} emails · {newAccounts} new ·{' '}
                {countOf(preview, 'trial_now', 'skipped_paid', 'skipped_team')}{' '}
                already have accounts · {countOf(preview, 'invalid')} invalid
                {countOf(preview, 'duplicate') > 0 &&
                  ` · ${countOf(preview, 'duplicate')} duplicate`}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {preview.map((result, index) => (
                  <span
                    key={index}
                    title={OUTCOMES[result.outcome].label}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                      OUTCOMES[result.outcome].className
                    )}
                  >
                    {result.email}
                    <button
                      type="button"
                      aria-label={`Remove ${result.email}`}
                      onClick={() =>
                        check(
                          checkedEmails.filter((_, i) => i !== index).join('\n')
                        )
                      }
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </>
          )}
          {dryRun.error && (
            <p className="text-sm text-destructive">{dryRun.error.message}</p>
          )}
          {dryRun.isFetching && (
            <p className="text-sm text-muted-foreground">Checking emails...</p>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Plan</p>
            <Select value={plan} onChange={(e) => setPlan(e.target.value)}>
              {PLAN_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Free until</p>
            <SegmentButton<TrialLength>
              options={[
                { value: 14, label: '14 days' },
                { value: 30, label: '30 days' },
                { value: 60, label: '60 days' },
                { value: 'custom', label: 'Custom date' },
              ]}
              selected={trialLength}
              onSelect={setTrialLength}
            />
            {trialLength === 'custom' ? (
              <Input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Ends {trialEndLabel}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Billing</p>
            <SegmentButton<'own' | 'team'>
              options={[
                { value: 'own', label: 'Each person on their own plan' },
                { value: 'team', label: 'Together on one team' },
              ]}
              selected="own"
              onSelect={(value) => value === 'team' && onChooseTeam()}
            />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-2 text-sm">
          {newAccounts > 0 && (
            <p>
              {people(newAccounts)} {newAccounts === 1 ? 'gets' : 'get'} a new
              account with {planLabel(plan)} free until {trialEndLabel}.
            </p>
          )}
          {trialNow > 0 && (
            <p>
              {people(trialNow)} who already{' '}
              {trialNow === 1 ? 'has an account gets' : 'have accounts get'} the
              trial now.
            </p>
          )}
          {skippedPaid > 0 && (
            <p>
              {people(skippedPaid)} on a paid plan{' '}
              {skippedPaid === 1 ? 'is' : 'are'} skipped.
            </p>
          )}
          {skippedTeam > 0 && (
            <p>
              {people(skippedTeam)} already on a team{' '}
              {skippedTeam === 1 ? 'is' : 'are'} skipped.
            </p>
          )}
          {unusable > 0 && (
            <p>
              {unusable} invalid or duplicate{' '}
              {unusable === 1 ? 'email is' : 'emails are'} skipped.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-between gap-2 border-t pt-4">
        <Button
          variant="outline"
          disabled={step === 1}
          onClick={() => setStep(step - 1)}
        >
          Back
        </Button>
        {step < 3 ? (
          <Button
            disabled={
              (step === 1 && newAccounts + trialNow === 0) ||
              (step === 2 && !trialEnd)
            }
            onClick={() => setStep(step + 1)}
          >
            Next
          </Button>
        ) : (
          <Button
            disabled={addPeople.isPending || newAccounts + trialNow === 0}
            onClick={handleConfirm}
          >
            {addPeople.isPending
              ? 'Adding...'
              : `Add ${people(newAccounts + trialNow)}`}
          </Button>
        )}
      </div>
    </div>
  );
}
